/**
 * Mobile haptic feedback and gesture utilities
 * Provides native-feeling interactions on mobile devices
 */

/**
 * Trigger haptic feedback if supported
 * @param {string} type - 'light', 'medium', 'heavy', 'success', 'warning', 'error'
 */
export const haptic = (type = 'light') => {
  // Check if vibration API is supported
  if (!navigator.vibrate) return;
  
  // Check if user prefers reduced motion
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  
  const patterns = {
    light: [10],           // Quick tap
    medium: [20],          // Standard tap
    heavy: [30],           // Strong tap
    success: [10, 50, 10], // Double tap for success
    warning: [20, 30, 20], // Alert pattern
    error: [50, 30, 50],   // Error pattern
    selection: [5],        // Very light for selections
    tick: [15, 30, 15],    // Satisfying tick for checking off items
  };
  
  navigator.vibrate(patterns[type] || patterns.light);
};

/**
 * Hook for swipe gesture detection
 * Returns handlers to attach to elements
 */
export const useSwipeGesture = (onSwipe, options = {}) => {
  const {
    threshold = 50,        // Minimum distance to trigger swipe
    restraint = 100,       // Maximum perpendicular distance
    allowedTime = 300,     // Maximum time for swipe
    direction = 'horizontal' // 'horizontal', 'vertical', or 'both'
  } = options;
  
  let startX = 0;
  let startY = 0;
  let startTime = 0;
  
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    startX = touch.clientX;
    startY = touch.clientY;
    startTime = Date.now();
  };
  
  const handleTouchEnd = (e) => {
    const touch = e.changedTouches[0];
    const distX = touch.clientX - startX;
    const distY = touch.clientY - startY;
    const elapsedTime = Date.now() - startTime;
    
    if (elapsedTime > allowedTime) return;
    
    // Horizontal swipe
    if (direction === 'horizontal' || direction === 'both') {
      if (Math.abs(distX) >= threshold && Math.abs(distY) <= restraint) {
        const swipeDir = distX > 0 ? 'right' : 'left';
        haptic('light');
        onSwipe?.(swipeDir, { distX, distY });
        return;
      }
    }
    
    // Vertical swipe
    if (direction === 'vertical' || direction === 'both') {
      if (Math.abs(distY) >= threshold && Math.abs(distX) <= restraint) {
        const swipeDir = distY > 0 ? 'down' : 'up';
        haptic('light');
        onSwipe?.(swipeDir, { distX, distY });
        return;
      }
    }
  };
  
  return {
    onTouchStart: handleTouchStart,
    onTouchEnd: handleTouchEnd
  };
};

/**
 * Create swipe handlers for a specific element
 * @param {function} onSwipeLeft - Callback for left swipe
 * @param {function} onSwipeRight - Callback for right swipe
 */
export const createSwipeHandlers = (onSwipeLeft, onSwipeRight, options = {}) => {
  const threshold = options.threshold || 50;
  const restraint = options.restraint || 100;
  
  let startX = 0;
  let startY = 0;
  let currentX = 0;
  let isDragging = false;
  
  return {
    onTouchStart: (e) => {
      const touch = e.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      currentX = 0;
      isDragging = true;
    },
    
    onTouchMove: (e) => {
      if (!isDragging) return;
      
      const touch = e.touches[0];
      const distY = Math.abs(touch.clientY - startY);
      
      // If vertical movement is too large, cancel horizontal swipe
      if (distY > restraint) {
        isDragging = false;
        return;
      }
      
      currentX = touch.clientX - startX;
    },
    
    onTouchEnd: () => {
      if (!isDragging) return;
      isDragging = false;
      
      if (Math.abs(currentX) >= threshold) {
        if (currentX > 0) {
          haptic('success');
          onSwipeRight?.();
        } else {
          haptic('success');
          onSwipeLeft?.();
        }
      }
    }
  };
};

/**
 * Button with haptic feedback
 * Wrap onClick to add haptic
 */
export const withHaptic = (onClick, type = 'light') => {
  return (e) => {
    haptic(type);
    onClick?.(e);
  };
};

export default {
  haptic,
  useSwipeGesture,
  createSwipeHandlers,
  withHaptic
};
